import { MouseEventHandler, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { DeleteIcon } from '@treams/treams-icons';

import { PencilEditBoxIcon } from '../icons';
import { Separator } from './Separator';
import { Spinner } from './Spinner';
import { Heading3, Paragraph } from './Text';
import { Button } from './button/Button';
import { IconButton } from './button/IconButton';

interface IProps {
	title?: string;
	description?: string;
	toolbar?: ReactNode;
	subheader?: ReactNode;
	/** Draw the section rule above the heading instead of treating this as the first block. */
	divided?: boolean;
}

const Description = styled(Paragraph)`
	max-width: 500px;
`;

export const SettingsEmptyState = styled(Paragraph)`
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${(props) => props.theme.colors.lightBlueDark};
	text-align: center;
	height: 100px;
`;

export const SettingsRow = (
	props: PropsWithChildren<{
		px?: number;
		borderless?: boolean;
		loading?: boolean;
		valid?: boolean;
		onEdit?: MouseEventHandler<HTMLElement>;
		onRestore?: MouseEventHandler<HTMLElement>;
		onSave?: MouseEventHandler<HTMLElement>;
		onDelete?: MouseEventHandler<HTMLElement>;
	}>,
) => {
	const { t } = useTranslation();

	return (
		<>
			<Flex py="18px" px={props.px ?? 3} style={{ minHeight: 64 }} alignItems="center">
				<Flex flexDirection="row" alignSelf="center" flex={1}>
					<Box flexGrow={1}>
						{typeof props.children === 'string' ? (
							<Heading3 mb={0}>{props.children}</Heading3>
						) : (
							props.children
						)}
					</Box>
				</Flex>
				{(props.loading || props.onEdit || props.onRestore || props.onSave || props.onDelete) && (
					<Flex alignSelf="center" ml={4}>
						{props.loading ? (
							<Spinner />
						) : (
							<>
								{props.onRestore && (
									<Box ml="12px">
										<Button
											title={t('common.restore')}
											onClick={props.onRestore}
											small
											type="transparent"
										/>
									</Box>
								)}

								{props.onSave && (
									<Box ml="12px">
										<Button
											title={t('common.save')}
											onClick={props.onSave}
											disabled={props.valid}
										/>
									</Box>
								)}

								{props.onEdit && (
									<Box ml="12px">
										<IconButton
											icon={<PencilEditBoxIcon />}
											onClick={props.onEdit}
											type="secondary"
										/>
									</Box>
								)}

								{props.onDelete && (
									<Box ml="12px">
										<IconButton icon={<DeleteIcon />} onClick={props.onDelete} type="destructive" />
									</Box>
								)}
							</>
						)}
					</Flex>
				)}
			</Flex>

			{props.borderless !== true && <Separator />}
		</>
	);
};

export function SettingsTable({
	title,
	children,
	description,
	toolbar,
	subheader,
	divided,
}: PropsWithChildren<IProps>): ReactElement {
	return (
		<>
			{divided && <Separator />}
			{(title || description) && (
				<Flex pt={divided ? 3 : 2} pb={3} alignItems="center">
					<Box flexGrow={1} flexShrink={1}>
						<Heading3 mb={0}>{title}</Heading3>
						<Description>{description}</Description>
					</Box>
					{toolbar}
				</Flex>
			)}
			{subheader && <Box mb={3}>{subheader}</Box>}
			<Flex flexDirection="column">
				<Separator />
				{children}
			</Flex>
		</>
	);
}
